import type { ConfigData, AffixDef } from '../../../types';
import { formatElement, formatTagDamageLabel } from '../../components/GameTerms';
import { getConfig, escapeHtml, formatStat, formatCategoryLabel } from './types';

export interface AffixesState {
  searchTerm: string;
  baseFilter: string;  // item base ID or 'unit'
  levelFilter: number;  // 0 = all levels
  expandedAffixIds: Set<string>;
}

export function createAffixesState(): AffixesState {
  return { searchTerm: '', baseFilter: '', levelFilter: 0, expandedAffixIds: new Set() };
}

export function renderAffixes(content: HTMLElement, state: AffixesState): void {
  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  // Build base selector options grouped by category
  const bases = Object.values(config.itemBases);
  const weapons = bases.filter(b => b.category === 'weapon');
  const armors = bases.filter(b => b.category === 'armor');
  const accessories = bases.filter(b => b.category === 'accessory');

  const baseOption = (id: string, name: string) =>
    `<option value="${id}"${state.baseFilter === id ? ' selected' : ''}>${name}</option>`;

  content.innerHTML = `
    <h2 class="codex-section-title">Affix Encyclopedia</h2>
    <p class="codex-section-subtitle">Select an item base to see which affixes can roll on it.</p>
    <div class="codex-filters">
      <select class="codex-filter-select codex-base-select" id="codex-affix-base">
        <option value="">All Items</option>
        <optgroup label="Weapons">
          ${weapons.map(b => baseOption(b.id, b.name)).join('')}
        </optgroup>
        <optgroup label="Armor">
          ${armors.map(b => baseOption(b.id, b.name)).join('')}
        </optgroup>
        <optgroup label="Accessories">
          ${accessories.map(b => baseOption(b.id, b.name)).join('')}
        </optgroup>
        <optgroup label="Other">
          ${baseOption('unit', 'Strands')}
        </optgroup>
      </select>
      <div class="codex-level-filter">
        <label for="codex-affix-level">Level</label>
        <input type="range" id="codex-affix-level" class="codex-level-slider" min="0" max="50" step="1" value="${state.levelFilter}" />
        <span id="codex-level-display" class="codex-level-display">${state.levelFilter || 'Any'}</span>
      </div>
      <input type="text" class="codex-filter-input" id="codex-affix-search" placeholder="Search..." value="${escapeHtml(state.searchTerm)}" />
    </div>
    <div id="codex-affix-results"></div>
  `;

  const baseSelect = content.querySelector('#codex-affix-base') as HTMLSelectElement;
  const levelSlider = content.querySelector('#codex-affix-level') as HTMLInputElement;
  const levelDisplay = content.querySelector('#codex-level-display') as HTMLElement;
  const searchInput = content.querySelector('#codex-affix-search') as HTMLInputElement;

  baseSelect?.addEventListener('change', () => {
    state.baseFilter = baseSelect.value;
    renderAffixResults(content, state, config);
  });
  levelSlider?.addEventListener('input', () => {
    state.levelFilter = parseInt(levelSlider.value, 10);
    levelDisplay.textContent = state.levelFilter ? String(state.levelFilter) : 'Any';
    renderAffixResults(content, state, config);
  });
  searchInput?.addEventListener('input', () => {
    state.searchTerm = searchInput.value;
    renderAffixResults(content, state, config);
  });

  renderAffixResults(content, state, config);
}

/** Get the tag set for the selected base filter */
function getBaseFilterTags(config: ConfigData, baseFilter: string): Set<string> | null {
  if (!baseFilter) return null;
  if (baseFilter === 'unit') return new Set(['unit']);
  const base = config.itemBases[baseFilter];
  return base ? new Set(base.tags) : null;
}

function filterAffixesByContext(affixes: AffixDef[], tags: Set<string> | null, level: number, searchTerm: string): AffixDef[] {
  let result = affixes;

  // Filter by tag match (same logic as rollExplicitAffixes)
  if (tags) {
    result = result.filter(a => a.tags.some(t => tags.has(t)));
  }

  // Filter by level — affix must have at least one tier at or below the level
  if (level > 0) {
    result = result.filter(a => a.tiers.some(t => t.requiredLevel <= level));
  }

  // Search filter
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    result = result.filter(a =>
      a.name.toLowerCase().includes(term) ||
      a.stat.toLowerCase().includes(term) ||
      a.id.toLowerCase().includes(term)
    );
  }

  return result;
}

function renderAffixResults(content: HTMLElement, state: AffixesState, config: ConfigData): void {
  const resultsEl = content.querySelector('#codex-affix-results') as HTMLElement;
  if (!resultsEl) return;

  const allAffixes = Object.values(config.affixes);
  const tags = getBaseFilterTags(config, state.baseFilter);
  const level = state.levelFilter;
  const eligible = filterAffixesByContext(allAffixes, tags, level, state.searchTerm);

  // Implicit for selected base
  let implicitHtml = '';
  if (state.baseFilter && state.baseFilter !== 'unit') {
    const base = config.itemBases[state.baseFilter];
    const implicitAffix = base ? config.affixes[base.implicitAffixId] : null;
    if (implicitAffix) {
      implicitHtml = `<div class="codex-affix-implicit">
        <div class="codex-affix-column-header">IMPLICIT</div>
        <div class="codex-affix-row-v2">
          <div class="codex-affix-desc-cell">
            <span class="codex-affix-desc-name">${implicitAffix.name}</span>
            <span class="codex-affix-desc-stat">${formatStatDesc(implicitAffix, config)}</span>
            <div class="codex-affix-tag-pills">${renderAffixTagPills(implicitAffix)}</div>
          </div>
          <div class="codex-affix-tier-values">${implicitAffix.tiers.map((t, i) =>
            `<span class="codex-tier-pill">T${i+1}: ${t.minValue}–${t.maxValue} <span class="codex-tier-lvl">(Lv${t.requiredLevel}+)</span></span>`
          ).join('')}</div>
        </div>
      </div>`;
    }
  }

  /** Sum of eligible tier weights for an affix (this is its true "presence" in the pool) */
  const affixPoolWeight = (a: AffixDef) => {
    const tiers = level > 0 ? a.tiers.filter(t => t.requiredLevel <= level) : a.tiers;
    return tiers.reduce((s, t) => s + (t.weight ?? a.weight), 0);
  };

  const prefixes = eligible.filter(a => a.type === 'prefix').sort((a, b) => affixPoolWeight(b) - affixPoolWeight(a));
  const suffixes = eligible.filter(a => a.type === 'suffix').sort((a, b) => affixPoolWeight(b) - affixPoolWeight(a));

  const prefixTotalWeight = prefixes.reduce((s, a) => s + affixPoolWeight(a), 0);
  const suffixTotalWeight = suffixes.reduce((s, a) => s + affixPoolWeight(a), 0);
  const grandTotal = prefixTotalWeight + suffixTotalWeight;
  const prefixTotalTiers = prefixes.reduce((s, a) => s + a.tiers.length, 0);
  const suffixTotalTiers = suffixes.reduce((s, a) => s + a.tiers.length, 0);

  const renderColumn = (label: string, affixes: AffixDef[], typeWeight: number, totalTiers: number, typeLabel: string) => {
    const affixPct = grandTotal > 0 ? (typeWeight / grandTotal * 100).toFixed(1) : '0';
    return `
      <div class="codex-affix-column">
        <div class="codex-affix-column-header">${label}</div>
        <div class="codex-affix-totals-row">
          <span class="codex-affix-totals-label">All modifiers</span>
          <span class="codex-affix-totals-val">${totalTiers} tiers</span>
          <span class="codex-affix-totals-val">${typeWeight} wt</span>
          <span class="codex-affix-totals-val">${affixPct}%</span>
        </div>
        ${affixes.length === 0
          ? '<div class="codex-affix-empty">No matching affixes</div>'
          : `<table class="codex-affix-table-v2">
              <thead><tr>
                <th class="col-base">BASE</th>
                <th class="col-num">Tiers</th>
                <th class="col-num">Level</th>
                <th class="col-num">Weight</th>
                <th class="col-num">${typeLabel} %</th>
                <th class="col-num">Wt %</th>
              </tr></thead>
              <tbody>${affixes.map(a => renderAffixRowV2(a, typeWeight, grandTotal, level, state, config)).join('')}</tbody>
            </table>`
        }
      </div>`;
  };

  resultsEl.innerHTML = `
    ${implicitHtml}
    <div class="codex-affix-columns">
      ${renderColumn('PREFIXES', prefixes, prefixTotalWeight, prefixTotalTiers, 'Prefix')}
      ${renderColumn('SUFFIXES', suffixes, suffixTotalWeight, suffixTotalTiers, 'Suffix')}
    </div>`;

  // Bind expand toggles
  resultsEl.querySelectorAll('[data-affix-toggle]').forEach(row => {
    row.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.codex-affix-expand-btn')) return;
      const id = (row as HTMLElement).dataset.affixToggle!;
      toggleAffix(id, content, state, config);
    });
  });
  resultsEl.querySelectorAll('.codex-affix-expand-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.affixId!;
      toggleAffix(id, content, state, config);
    });
  });
}

function toggleAffix(id: string, content: HTMLElement, state: AffixesState, config: ConfigData): void {
  if (state.expandedAffixIds.has(id)) state.expandedAffixIds.delete(id);
  else state.expandedAffixIds.add(id);
  renderAffixResults(content, state, config);
}

function renderAffixRowV2(a: AffixDef, typeWeight: number, grandWeight: number, level: number, state: AffixesState, config: ConfigData): string {
  const isExpanded = state.expandedAffixIds.has(a.id);
  const availTiers = level > 0 ? a.tiers.filter(t => t.requiredLevel <= level) : a.tiers;
  const tierCount = availTiers.length;
  const maxIlvl = availTiers.length > 0 ? availTiers[availTiers.length - 1].requiredLevel : 0;
  const poolWeight = availTiers.reduce((s, t) => s + (t.weight ?? a.weight), 0);
  const typePct = typeWeight > 0 ? (poolWeight / typeWeight * 100).toFixed(1) : '0';
  const wtPct = grandWeight > 0 ? (poolWeight / grandWeight * 100).toFixed(1) : '0';
  const tagPills = renderAffixTagPills(a);

  let html = `<tr class="codex-affix-row-main${isExpanded ? ' expanded' : ''}" data-affix-toggle="${a.id}">
    <td class="col-base">
      <div class="codex-affix-desc-name">${a.name}</div>
      <div class="codex-affix-desc-stat">${formatStatDesc(a, config)}</div>
      ${tagPills ? `<div class="codex-affix-tag-pills">${tagPills}</div>` : ''}
    </td>
    <td class="col-num">${tierCount}</td>
    <td class="col-num">${maxIlvl}</td>
    <td class="col-num">${poolWeight}</td>
    <td class="col-num">${typePct}%</td>
    <td class="col-num">${wtPct}%</td>
  </tr>`;

  if (isExpanded) {
    for (let i = 0; i < a.tiers.length; i++) {
      const t = a.tiers[i];
      const avail = level === 0 || t.requiredLevel <= level;
      const tierWt = t.weight ?? a.weight;
      html += `<tr class="codex-affix-tier-row${avail ? '' : ' locked'}">
        <td class="col-base"><span class="codex-affix-tier-label">T${i+1}:</span> ${t.minValue}–${t.maxValue}</td>
        <td class="col-num"></td>
        <td class="col-num">${t.requiredLevel}</td>
        <td class="col-num">${tierWt}</td>
        <td class="col-num" colspan="2"></td>
      </tr>`;
    }
  }

  return html;
}

function formatStatDesc(a: AffixDef, config: ConfigData): string {
  // Mastery affixes — placeholder uses the mastery name, not the stat key.
  // Matches the gear display in AffixDisplay.formatAffixValue (no "to" prefix).
  if (a.category === 'mastery') {
    const name = config.masteries?.[a.masteryId ?? '']?.name ?? a.masteryId ?? '';
    return a.modifier === 'percent' ? `+#% ${name}` : `+# ${name}`;
  }
  if (a.category === 'mastery_cost_reduction') {
    const name = config.masteries?.[a.masteryId ?? '']?.name ?? a.masteryId ?? '';
    return `-#x ${name} Cost`;
  }
  // Tag-increased — match gear display ("+#% increased <descriptor>").
  if (a.category === 'tag_increased') {
    return `+#% increased ${formatTagDamageLabel(a.conditionTag ?? '')}`;
  }
  // Flat damage — show the range form ("Adds # to # <Element> Damage").
  if (a.category === 'flat_damage' && a.element) {
    return `Adds # to # ${formatElement(a.element)} Damage`;
  }
  const stat = formatStat(a.stat);
  return a.modifier === 'percent' ? `+#% ${stat}` : `+# ${stat}`;
}

function renderAffixTagPills(a: AffixDef): string {
  const pills: string[] = [];
  // Element pill only — category pills are internal noise, not useful to players
  if (a.element) {
    pills.push(`<span class="codex-affix-pill el-${a.element}">${formatElement(a.element)}</span>`);
  }
  // Condition tag for tag_increased affixes (e.g., "Melee", "Spell", "DoT")
  if (a.conditionTag) {
    const label = a.conditionTag.charAt(0).toUpperCase() + a.conditionTag.slice(1);
    pills.push(`<span class="codex-affix-pill cat-tag_increased">${label}</span>`);
  }
  return pills.join('');
}
